// Profile API client. Same token key the old site used, so a browser that was
// signed in there is not confused; a fresh sign-in is still required because
// the token spaces differ.
import type { Layout } from "./state/types";

export const TOKEN_KEY = "dm-screen-instance-session";
export interface Profile { id: string; username: string; displayName: string }
export interface AuthResult { token: string; profile: Profile; layout: Layout | null }

// A profile is only usable if it actually carries an id. A valid-JSON 200 that
// nonetheless lacks a profile (a server shape change, an auth edge) must not reach
// the app as a session, or it blank-crashes on session.profile.id — the auth flow
// treats "no valid profile" as "not signed in" and returns to the login screen.
export const validProfile = (p: unknown): p is Profile =>
  !!p && typeof (p as { id?: unknown }).id === "string" && (p as { id: string }).id.length > 0;

export class ApiError extends Error {
  constructor(message: string, public status: number, public body: unknown) { super(message); }
}

export const getToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; } };
export const setToken = (token: string) => { try { token ? localStorage.setItem(TOKEN_KEY, token) : localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ } };

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { "content-type": "application/json", ...(init.headers as Record<string, string> | undefined) };
  if (token) headers.authorization = `Bearer ${token}`;
  const response = await fetch(path, { ...init, headers });
  const text = await response.text();
  let body: any = null;
  let parseFailed = false;
  try { body = text ? JSON.parse(text) : null; } catch { parseFailed = true; body = { error: text.slice(0, 200) }; }
  if (!response.ok) throw new ApiError(body?.error || `${response.status} ${response.statusText}`, response.status, body);
  // A 200 whose body is not JSON is NOT a success: a proxy error page, a captive
  // portal, or a static host's SPA fallback can serve index.html with status 200.
  // Returning that as T hands the caller a profile-less object and blank-crashes a
  // brand-new user on session.profile.id — so treat it as an error and let the auth
  // flow fall back to the login screen instead.
  if (parseFailed) throw new ApiError("The server returned an unexpected response (not JSON) — you may be offline or behind a sign-in page.", response.status, body);
  return body as T;
}

export const login = (username: string, password: string) => api<AuthResult>("/api/profiles/login", { method: "POST", body: JSON.stringify({ username, password }) });
export const register = (username: string, password: string, displayName: string) => api<AuthResult>("/api/profiles/register", { method: "POST", body: JSON.stringify({ username, password, displayName }) });
export const me = () => api<{ profile: Profile; layout: Layout | null; revision: number; updatedAt: string | null }>("/api/profiles/me");
export const logout = () => api<{ ok: true }>("/api/profiles/logout", { method: "POST" }).catch(() => ({ ok: true as const }));
export const saveLayout = (layout: Layout, revision: number) => api<{ ok: true; revision: number; updatedAt: string }>("/api/profiles/layout", { method: "PUT", body: JSON.stringify({ layout, revision }) });

// Inventory snapshots: one per campaign + target (character id or "party").
export interface InventorySnapshotRecord {
  campaignKey: string; targetKey: string; campaignName: string; schemaVersion: number; revision: number;
  capturedAt: string; updatedAt: string; snapshotBytes?: number; snapshot: Record<string, any>;
}
export const listSnapshots = () => api<{ snapshots: InventorySnapshotRecord[] }>("/api/profiles/inventory-snapshots");
// listSnapshots can resolve to null (an empty 200) or a 200 whose body lacks `snapshots`;
// a consumer that reads `.snapshots` and renders/iterates it would then throw. Coerce to an
// array at the boundary so every caller is safe.
export const snapshotRecords = (r: { snapshots?: InventorySnapshotRecord[] } | null | undefined): InventorySnapshotRecord[] =>
  Array.isArray(r?.snapshots) ? (r as { snapshots: InventorySnapshotRecord[] }).snapshots : [];
export const putSnapshots = (snapshots: Omit<InventorySnapshotRecord, "updatedAt" | "snapshotBytes">[]) => api<{ ok: true; saved: number; updatedAt: string }>("/api/profiles/inventory-snapshots", { method: "PUT", body: JSON.stringify({ snapshots }) });
